import { useState } from "react";
import axios from "axios";
import { useAuth, API } from "../../App";
import { toast } from "sonner";
import { recentSeasons, currentSeason } from "../../utils/seasons";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Loader2, Sparkles, CheckCircle2, X, Save, Trash2 } from "lucide-react";

const TEAMS = ["First Team", "Women", "U21", "U18"];
const TEAM_LABELS = {
  "First Team": "Men's First Team",
  Women: "Women's First Team",
  U21: "Men's U21",
  U18: "Men's U18",
};
const SEASONS = recentSeasons(3);
const COMPETITIONS = [
  "Premier League",
  "Championship",
  "FA Cup",
  "EFL Cup",
  "Europa Conference League",
  "Europa League",
  "Champions League",
  "WSL",
  "FA Cup (Women)",
  "Women's League Cup",
  "Premier League 2",
  "U18 Premier League",
  "FA Youth Cup",
  "EFL Trophy",
  "Club Friendlies",
];

const PLACEHOLDER = `Paste the fixture list here — any format works. Examples:

Sat 16 Aug 2026 vs Wolves (H) 3pm — Premier League
23/08/2026 Man City (A) 5:30pm PL
30 Aug — Chelsea away — 12.30pm — Prem
13 Sept 15:00 Tottenham H, Premier League
21 Sept Arsenal (Away) 16:30

The AI will convert this into structured fixtures — you can review and edit before saving.`;

/**
 * Bulk paste-in fixture importer. Admin picks the team + season, pastes raw
 * text, hits "Parse with AI" → Gemini 3 Flash returns a strict list of
 * fixtures which we render as an editable preview table. Confirming writes
 * every row with `manually_edited=True`, so the football-api scheduled sync
 * will leave the admin's dates/times alone going forward.
 */
export default function FixturesBulkPaste({ onImported }) {
  const { token } = useAuth();
  const [team, setTeam] = useState("First Team");
  const [season, setSeason] = useState(currentSeason());
  const [defaultComp, setDefaultComp] = useState("Premier League");
  const [pastedText, setPastedText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState([]); // parsed rows (editable)
  const [previewErrors, setPreviewErrors] = useState([]);

  const handleParse = async () => {
    if (!pastedText.trim()) {
      toast.error("Paste some fixtures first");
      return;
    }
    setParsing(true);
    try {
      const res = await axios.post(
        `${API}/admin/fixtures/bulk-parse`,
        {
          team,
          season,
          pasted_text: pastedText,
          default_competition: defaultComp || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const rows = res.data.fixtures || [];
      setPreview(rows);
      setPreviewErrors([]);
      if (rows.length === 0) {
        toast.warning("AI couldn't parse any fixtures from that text — try being more explicit about dates/opponents");
      } else {
        toast.success(`Parsed ${rows.length} fixture${rows.length === 1 ? "" : "s"} — review below and Save`);
      }
    } catch (e) {
      toast.error(`Parse failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setParsing(false);
    }
  };

  const updateRow = (idx, field, value) => {
    setPreview((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const removeRow = (idx) => {
    setPreview((rows) => rows.filter((_, i) => i !== idx));
  };

  const handleImport = async () => {
    if (preview.length === 0) {
      toast.error("Nothing to import");
      return;
    }
    setImporting(true);
    try {
      const res = await axios.post(
        `${API}/admin/fixtures/bulk-import`,
        { team, season, fixtures: preview },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { added, updated, skipped, errors } = res.data;
      toast.success(`Imported ${added + updated} fixtures (${added} new, ${updated} updated)${skipped ? `, ${skipped} skipped` : ""}`);
      setPreviewErrors(errors || []);
      // Clear the paste box and preview on success so admin can move on
      setPastedText("");
      setPreview([]);
      if (onImported) onImported();
    } catch (e) {
      toast.error(`Import failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setPastedText("");
    setPreview([]);
    setPreviewErrors([]);
  };

  return (
    <div
      className="bg-gradient-to-br from-violet-50 to-indigo-50 rounded-xl p-5 mb-6 border-2 border-violet-200"
      data-testid="fixtures-bulk-paste"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-bold text-slate-800 flex items-center gap-2 text-lg">
            <Sparkles className="w-5 h-5 text-violet-600" />
            Bulk Paste-In Import
            <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-violet-600 text-white">AI</span>
          </h3>
          <p className="text-sm text-slate-600 mt-1">
            Paste a schedule from anywhere (club site, PDF, tweet, WhatsApp) and Gemini 3 Flash will convert it into
            fixtures. Every row is stamped as <span className="font-mono text-xs">manually_edited</span> so the
            scheduled scraper will not overwrite it.
          </p>
        </div>
        {(pastedText || preview.length > 0) && (
          <Button
            onClick={handleReset}
            variant="ghost"
            size="sm"
            className="text-slate-500"
            data-testid="bulk-paste-reset"
          >
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
        )}
      </div>

      {/* Meta selectors */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div>
          <Label className="text-xs font-semibold uppercase text-slate-500">Team</Label>
          <Select value={team} onValueChange={setTeam}>
            <SelectTrigger data-testid="bulk-paste-team-select" className="mt-1 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TEAMS.map((t) => (
                <SelectItem key={t} value={t} data-testid={`bulk-paste-team-${t}`}>
                  {TEAM_LABELS[t]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase text-slate-500">Season</Label>
          <Select value={season} onValueChange={setSeason}>
            <SelectTrigger data-testid="bulk-paste-season-select" className="mt-1 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SEASONS.map((s) => (
                <SelectItem key={s} value={s} data-testid={`bulk-paste-season-${s}`}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase text-slate-500">Default competition (fallback)</Label>
          <Select value={defaultComp} onValueChange={setDefaultComp}>
            <SelectTrigger data-testid="bulk-paste-competition-select" className="mt-1 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COMPETITIONS.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Paste box */}
      <div className="mb-3">
        <Textarea
          value={pastedText}
          onChange={(e) => setPastedText(e.target.value)}
          placeholder={PLACEHOLDER}
          rows={10}
          className="font-mono text-sm bg-white"
          data-testid="bulk-paste-textarea"
        />
      </div>

      <div className="flex gap-2">
        <Button
          onClick={handleParse}
          disabled={parsing || !pastedText.trim()}
          className="bg-violet-600 hover:bg-violet-700"
          data-testid="bulk-paste-parse-btn"
        >
          {parsing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Parsing with AI…
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" /> Parse with AI
            </>
          )}
        </Button>
      </div>

      {/* Preview table */}
      {preview.length > 0 && (
        <div className="mt-5 bg-white rounded-lg p-4 border border-violet-200" data-testid="bulk-paste-preview">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-slate-800 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Preview ({preview.length} row{preview.length === 1 ? "" : "s"}) — edit before saving
            </h4>
            <Button
              onClick={handleImport}
              disabled={importing}
              className="bg-green-600 hover:bg-green-700"
              data-testid="bulk-paste-save-btn"
            >
              {importing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" /> Confirm &amp; Save All
                </>
              )}
            </Button>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[24%]">Opponent</TableHead>
                  <TableHead className="w-[14%]">Date</TableHead>
                  <TableHead className="w-[10%]">Time</TableHead>
                  <TableHead className="w-[12%]">H/A</TableHead>
                  <TableHead className="w-[30%]">Competition</TableHead>
                  <TableHead className="w-[10%]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.map((row, idx) => (
                  <TableRow key={idx} data-testid={`bulk-paste-preview-row-${idx}`}>
                    <TableCell>
                      <Input
                        value={row.opponent || ""}
                        onChange={(e) => updateRow(idx, "opponent", e.target.value)}
                        className="h-8 text-sm"
                        data-testid={`bulk-paste-opponent-${idx}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="date"
                        value={row.date || ""}
                        onChange={(e) => updateRow(idx, "date", e.target.value)}
                        className="h-8 text-sm"
                        data-testid={`bulk-paste-date-${idx}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="time"
                        value={row.time || ""}
                        onChange={(e) => updateRow(idx, "time", e.target.value || null)}
                        className="h-8 text-sm"
                        data-testid={`bulk-paste-time-${idx}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={row.home_away || "Home"}
                        onValueChange={(v) => updateRow(idx, "home_away", v)}
                      >
                        <SelectTrigger className="h-8 text-sm" data-testid={`bulk-paste-ha-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Home">Home</SelectItem>
                          <SelectItem value="Away">Away</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        value={row.competition || ""}
                        onChange={(e) => updateRow(idx, "competition", e.target.value)}
                        className="h-8 text-sm"
                        data-testid={`bulk-paste-competition-${idx}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        onClick={() => removeRow(idx)}
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                        data-testid={`bulk-paste-remove-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {previewErrors.length > 0 && (
            <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
              <strong>Notes:</strong>
              <ul className="list-disc list-inside mt-1">
                {previewErrors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
